import type { ReactNode } from 'react';
import { cn } from './utils';

/**
 * Shared status-pill primitive — the `rounded-full`, tinted-bed, 1px
 * inset-ring chip DESIGN.md's Chips section describes ("tinted bed with
 * same-hue ink and a 1px inset ring"). Mirrors the same tinted-hue
 * convention as `ConditionBadge` / `LifecycleBadge` elsewhere in this
 * package, so a single primitive now backs every enum/category/count pill
 * across both portals instead of each call site re-implementing the same
 * markup with slightly different classNames.
 *
 * `neutral` is the only tone with no state meaning — reach for it for
 * categories ("html"/"pdf", "SKU catalog") and counts, never for a verdict.
 */

export type PillTone = 'green' | 'amber' | 'red' | 'neutral';

/**
 * Semantic tokens only — this primitive renders in both portals, and each
 * app wires `--status-green` / `--status-amber` / `--status-red` /
 * `--muted` / `--muted-foreground` / `--border` to its own theme. Literal
 * Tailwind ramp names (`bg-zinc-100`, `text-emerald-700`, …) used to sit
 * here instead; that only worked because both apps happened to hijack
 * those exact stock ramp names in their own `globals.css`, and the two
 * hijacks are not the same color — ops maps `zinc-600` to `muted-foreground`
 * (58% lightness) while the customer portal maps it to a different register
 * (72% lightness), so the same pill rendered a third grey in one portal.
 * Semantic tokens fix that by construction.
 */
const TONE_STYLES: Record<PillTone, string> = {
  green: 'bg-status-green/10 text-status-green ring-status-green/30',
  amber: 'bg-status-amber/10 text-status-amber ring-status-amber/30',
  red: 'bg-status-red/10 text-status-red ring-status-red/30',
  neutral: 'bg-muted text-muted-foreground ring-border',
};

export function StatusPill({
  tone = 'neutral',
  title,
  className,
  children,
}: {
  tone?: PillTone;
  /** Tooltip text — use for anything that isn't self-explanatory from the label alone. */
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-medium ring-1 ring-inset',
        TONE_STYLES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
